import React, { useEffect, useState } from "react";
import AppShell from "../../components/layout/AppShell";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Download } from "@phosphor-icons/react";

function currentMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export default function OfficePayrollSummary() {
  const [month, setMonth] = useState(currentMonth());
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/payroll/office-summary?month=${month}`).then(r => setData(r.data))
      .catch(e => toast.error(formatApiError(e)));
  }, [month]);

  const rows = data?.rows || [];
  const totals = rows.reduce((a, r) => ({
    active: a.active + (r.active_hours || 0),
    ot: a.ot + (r.ot_hours || 0),
    ut: a.ut + (r.ut_hours || 0),
    gross: a.gross + (r.gross || 0),
  }), { active: 0, ot: 0, ut: 0, gross: 0 });

  const exportCsv = () => {
    const h = ["Name", "Code", "Role", "Days", "Active", "Break", "Idle", "OT", "UT", "Rate", "Hours Pay", "OT Bonus", "Base", "Gross"];
    const lines = [h.join(",")];
    rows.forEach(r => lines.push([r.name, r.staff_code || "", r.role, r.days_worked, r.active_hours, r.break_hours, r.inactive_hours, r.ot_hours, r.ut_hours, r.hourly_rate, r.hours_pay, r.ot_bonus, r.base_salary, r.gross].join(",")));
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `office_payroll_${month}.csv`;
    a.click();
  };

  return (
    <AppShell title="Office Payroll Summary" subtitle={`Month: ${month} · Payable = active time`}
      actions={
        <div className="flex gap-2">
          <input type="month" value={month} onChange={e => setMonth(e.target.value)}
                 data-testid="payroll-month-input"
                 className="h-10 px-3 border border-[#E5E2D9] rounded-md text-sm"/>
          <button onClick={exportCsv} data-testid="payroll-export"
                  className="inline-flex items-center gap-2 h-10 px-4 border border-[#E5E2D9] rounded-md text-sm">
            <Download size={14}/>Export
          </button>
        </div>
      }>
      <div className="grid grid-cols-4 gap-3 mb-4" data-testid="payroll-stats">
        <Stat label="Total Active Hrs" value={totals.active.toFixed(1)}/>
        <Stat label="OT Hrs" value={totals.ot.toFixed(1)} color="#2D5A27"/>
        <Stat label="UT Hrs" value={totals.ut.toFixed(1)} color="#C25D38"/>
        <Stat label="Gross Payout" value={`₹${totals.gross.toFixed(0)}`} highlight/>
      </div>

      <div className="bg-white border border-[#E5E2D9] rounded-md overflow-x-auto">
        <table className="w-full text-sm" data-testid="office-payroll-table">
          <thead>
            <tr className="bg-[#F7F6F2] text-left text-[11px] uppercase tracking-[0.2em] text-[#5C5A55]">
              <th className="px-4 py-3">Staff</th>
              <th className="px-4 py-3">Code</th>
              <th className="px-4 py-3">Role</th>
              <th className="px-4 py-3 text-right">Days</th>
              <th className="px-4 py-3 text-right">Active</th>
              <th className="px-4 py-3 text-right text-[#2D5A27]">OT</th>
              <th className="px-4 py-3 text-right text-[#C25D38]">UT</th>
              <th className="px-4 py-3 text-right">₹/hr</th>
              <th className="px-4 py-3 text-right">Hours Pay</th>
              <th className="px-4 py-3 text-right">OT Bonus</th>
              <th className="px-4 py-3 text-right">Base</th>
              <th className="px-4 py-3 text-right">Gross</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={12} className="px-6 py-12 text-center text-[#8A8780]">No data.</td></tr>}
            {rows.map((r, i) => (
              <tr key={r.user_id} className={i % 2 === 0 ? "bg-white" : "bg-[#FAFAF7]"}>
                <td className="px-4 py-3 font-medium">{r.name}</td>
                <td className="px-4 py-3 font-mono text-xs">{r.staff_code || "—"}</td>
                <td className="px-4 py-3 capitalize text-xs">{r.role}</td>
                <td className="px-4 py-3 text-right font-mono">{r.days_worked}</td>
                <td className="px-4 py-3 text-right font-mono">{Number(r.active_hours || 0).toFixed(1)}</td>
                <td className="px-4 py-3 text-right font-mono text-[#2D5A27]">{Number(r.ot_hours || 0).toFixed(1)}</td>
                <td className="px-4 py-3 text-right font-mono text-[#C25D38]">{Number(r.ut_hours || 0).toFixed(1)}</td>
                <td className="px-4 py-3 text-right font-mono">{r.hourly_rate}</td>
                <td className="px-4 py-3 text-right font-mono">₹{Number(r.hours_pay || 0).toFixed(0)}</td>
                <td className="px-4 py-3 text-right font-mono text-[#2D5A27]">₹{Number(r.ot_bonus || 0).toFixed(0)}</td>
                <td className="px-4 py-3 text-right font-mono">₹{Number(r.base_salary || 0).toFixed(0)}</td>
                <td className="px-4 py-3 text-right font-mono font-semibold text-[#2D5A27]">₹{Number(r.gross || 0).toFixed(0)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </AppShell>
  );
}

const Stat = ({ label, value, color, highlight }) => (
  <div className={`rounded-md p-4 border ${highlight ? "bg-[#2D5A27] text-white border-[#2D5A27]" : "bg-white border-[#E5E2D9]"}`}>
    <div className="text-[10px] uppercase tracking-[0.2em] opacity-70">{label}</div>
    <div className="font-heading text-3xl mt-1" style={{ color: !highlight ? (color || undefined) : undefined }}>{value}</div>
  </div>
);
